import {
  Icon,
  Text,
  HStack,
  useTheme,
  Box,
  Collapse,
  Button,
} from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { compareProps } from '../../../../../../tarifications/src/legacy/services/proFlowService';
import { Inputs } from '../../../../types/Enums';
import { NaceCode, NaceCodeList } from '../../../../types/CompanyScanTypes';
import { getFlowsDataTestId } from '../../../../utils/testUtils';
import NaceCodeTreeLine from './NaceCodeTreeLine';

interface Props {
  naceCode: NaceCodeList;
  selectedNaceCodes: NaceCode[];
  isParentOpen: boolean;
  onSelected: (nace: NaceCodeList) => void;
  onDeselected: (nace: NaceCodeList) => void;

  isOpenMapping: { [x: string]: boolean };
  onOpen: (id: string) => void;
  onClose: (id: string) => void;
}

export const NACE_TYPE_LEVEL_MAPPING = {
  1: 'departments',
  2: 'groups',
  3: 'classes',
  4: 'subclasses',
};

const NaceCodeTreeItem: React.FC<React.PropsWithChildren<Props>> = ({
  isParentOpen,
  selectedNaceCodes,
  naceCode,
  onSelected,
  onDeselected,

  isOpenMapping,
  onOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { sizes } = useTheme();
  const { code, label, level: levelString } = naceCode;

  const isSelected = selectedNaceCodes
    .map(({ code }) => code)
    .includes(naceCode.code);
  const isOpen = isOpenMapping[naceCode.code];

  // Element bounds
  const level = parseInt(levelString) - 1;
  const marginUnit = parseInt(sizes[2]);
  const itemMarginLeft = marginUnit * level;

  const handleOnButtonClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    if (isSelected) {
      onDeselected(naceCode);
    } else {
      onSelected(naceCode);
    }
  };

  const children: NaceCode[] =
    naceCode[NACE_TYPE_LEVEL_MAPPING[naceCode.level]];

  const handleOnItemClick = () => {
    if (isOpen) {
      onClose(naceCode.code);
    } else {
      onOpen(naceCode.code);
    }
    if ((children || []).length === 0) {
      handleOnButtonClick();
    }
  };

  return (
    <Collapse in={isParentOpen || level === 0}>
      <Box
        pt="2"
        pos="relative"
        data-test-id={getFlowsDataTestId(Inputs.NACE_CODE, 'NACE-ENTRY', code)}
      >
        {[...Array(level).keys()].map((index) => (
          <NaceCodeTreeLine
            key={index}
            isFirst={index === level - 1}
            left={marginUnit * index}
          />
        ))}
        <HStack
          bg={isSelected ? 'primary.50' : 'transparent'}
          spacing="5"
          pl={itemMarginLeft}
          onClick={handleOnItemClick}
          cursor="pointer"
          _hover={{
            div: { color: 'primary.500' },
            button: { opacity: '1' },
            ...(isSelected && {
              '> div:last-of-type': { opacity: '0' },
            }),
          }}
          transition="color 3s"
          justify="space-between"
          minH="8"
          borderRadius="sm"
        >
          <HStack
            my="1.5"
            spacing="2"
            color={isSelected ? 'primary.500' : 'text.gray'}
            align="flex-start"
          >
            <Box
              transform={isOpen ? 'rotate(90deg)' : 'unset'}
              transition="transform 0.2s"
              display={children?.length > 0 ? 'block' : 'none'}
            >
              <Icon name="GeneralActionsChevronRight" boxSize={4} />
            </Box>
            <Text textStyle="smallBody">
              {code}: {label}
            </Text>
          </HStack>
          {isSelected && (
            <Box pos="absolute" right="2" opacity="1" transition="0.2s">
              <Icon name="GeneralStatusSuccessCheckmark" color="primary.500" />
            </Box>
          )}
          <Button
            transition="0.2s"
            opacity="0"
            variant="ghost"
            size="sm"
            onClick={handleOnButtonClick}
            data-test-id={getFlowsDataTestId(
              Inputs.NACE_CODE,
              'NACE-BUTTON',
              code,
            )}
          >
            {isSelected ? t('DESELECT') : t('SELECT')}
          </Button>
        </HStack>
        {children?.length > 0 &&
          isParentOpen &&
          children.map((naceCode) => (
            <NaceCodeTreeItem
              key={naceCode.code}
              selectedNaceCodes={selectedNaceCodes}
              isParentOpen={isOpen}
              naceCode={naceCode}
              onSelected={onSelected}
              onDeselected={onDeselected}
              isOpenMapping={isOpenMapping}
              onOpen={onOpen}
              onClose={onClose}
            />
          ))}
      </Box>
    </Collapse>
  );
};

export default React.memo(NaceCodeTreeItem, compareProps);
